import { DITypes } from "@/core/@types/types";
import { type HttpClient } from "@/core/services/protocols/types";
import { inject, injectable, optional } from "inversify";
import {
  IAtualizarCertificadoGateway,
  IInserirCertificadoParams,
  IValidarCertificadoParams,
  IValidarCertificadoResponse
} from "./AtualizarCertificadoGateway";
import { FinancasGatewayError } from "@/core/services/financas/gateways/FinancasGatewayErrors";
import CancelationMiddleware from "@/core/services/CancelationMiddleware";
import type { IToastNotification } from "@/core/composables/notification/types";
import { diContainer } from "@/inversify.config";
import { IDefaultHorizonsResponse } from "@/core/services/financas/gateways/types";
import Result from "@/core/common/Result";

@injectable()
export class AtualizarCertificadoGatewayAdapter
  extends FinancasGatewayError
  implements IAtualizarCertificadoGateway
{
  @inject(DITypes.INDICADORES_HTTP_CLIENT)
  @optional()
  private http!: HttpClient;

  private readonly ERROR =
    "Não foi possível buscar informações sobre certificados";

  private validarCertificadoAbortController!: CancelationMiddleware;

  constructor(notification: IToastNotification<string>) {
    super(notification);

    this.validarCertificadoAbortController = diContainer.get(
      CancelationMiddleware
    );
  }

  async validarCertificado(
    params: IValidarCertificadoParams
  ): Promise<Result<IValidarCertificadoResponse>> {
    try {
      const signal = this.validarCertificadoAbortController.handleSignal();

      const response = await this.http.post<
        IDefaultHorizonsResponse<IValidarCertificadoResponse>
      >({
        url: "/financas/CertificadoDeEmpresa/v1/validar-certificado",
        body: params,
        config: {
          signal
        }
      });

      if (this.hasError(response)) {
        this.handleErrors(response, this.ERROR);

        return Result.fail(this.DEFAULT_ERROR);
      }

      return Result.ok(response.body?.result);
    } catch {
      return Result.fail(this.DEFAULT_ERROR);
    }
  }

  async inserirCertificado(
    params: IInserirCertificadoParams
  ): Promise<Result<void>> {
    try {
      const response = await this.http.post<IDefaultHorizonsResponse<unknown>>({
        url: "/financas/CertificadoDeEmpresa/v1/inserir-certificado",
        body: {
          ...params
        }
      });

      if (this.hasError(response)) {
        this.handleErrors(response, this.ERROR);

        return Result.fail(this.DEFAULT_ERROR);
      }

      return Result.ok();
    } catch {
      return Result.fail(this.DEFAULT_ERROR);
    }
  }
}
